import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';

// Async thunks for API calls
export const fetchClasses = createAsyncThunk('classes/fetchClasses', async () => {
  try {
    const response = await api.getClasses();
    return response.data;
  } catch (error) {
    // If API fails, return empty array (for local development)
    return [];
  }
});

export const createClass = createAsyncThunk('classes/createClass', async (classData) => {
  try {
    const response = await api.createClass(classData);
    return response.data;
  } catch (error) {
    throw error;
  }
});

export const updateClass = createAsyncThunk('classes/updateClass', async ({ id, data }) => {
  try {
    const response = await api.updateClass(id, data);
    return response.data;
  } catch (error) {
    throw error;
  }
});

export const deleteClass = createAsyncThunk('classes/deleteClass', async (id) => {
  try {
    await api.deleteClass(id);
    return id;
  } catch (error) {
    // For local development, still return id to remove from state
    return id;
  }
});

const classesSlice = createSlice({
  name: 'classes',
  initialState: {
    classes: [],
    loading: false,
    error: null,
  },
  reducers: {
    // For local state management (optimistic updates)
    addClassLocal: (state, action) => {
      const newClass = {
        ...action.payload,
        id: action.payload.id || `class-${Date.now()}`,
      };
      state.classes.push(newClass);
    },
    updateClassLocal: (state, action) => {
      const index = state.classes.findIndex(
        (c) => c.id === action.payload.id || c._id === action.payload.id
      );
      if (index !== -1) {
        state.classes[index] = { ...state.classes[index], ...action.payload };
      }
    },
    removeClassLocal: (state, action) => {
      state.classes = state.classes.filter(
        (c) => c.id !== action.payload && c._id !== action.payload
      );
    },
    initializeSampleData: (state, action) => {
      // Sample data for testing - only if no data exists and API is not available
      // This is now handled by saving to backend in App.jsx
      if (state.classes.length === 0) {
        state.classes = [
          {
            id: 'class-1',
            type: 'Bhangra',
            ageGroup: 'Teens',
            instructor: 'Pratap',
            day: 'Monday',
            startTime: '17:00',
            duration: 60,
            location: 'Studio A',
            notes: 'Focus on footwork this week',
            rumaalLevel: 'Green',
            name: 'Advanced Teens',
            students: [
              {
                id: 'student-1',
                name: 'Raj Patel',
                rumaalRank: 'Green',
                status: 'Active',
              },
              {
                id: 'student-2',
                name: 'Priya Singh',
                rumaalRank: 'Blue',
                status: 'Active',
              },
            ],
          },
          {
            id: 'class-2',
            type: 'Bollywood',
            ageGroup: 'Adults',
            instructor: 'Gugu',
            day: 'Wednesday',
            startTime: '19:00',
            duration: 90,
            location: 'Studio B',
            notes: '',
            rumaalLevel: 'Yellow',
            name: 'Bollywood Adults',
            students: [
              {
                id: 'student-3',
                name: 'Amit Kumar',
                rumaalRank: 'Yellow',
                status: 'Trial',
              },
            ],
          },
        ];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClasses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.loading = false;
        state.classes = action.payload;
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createClass.pending, (state) => {
        state.loading = true;
      })
      .addCase(createClass.fulfilled, (state, action) => {
        state.loading = false;
        const newClass = {
          ...action.payload,
          id: action.payload._id || action.payload.id,
        };
        // Remove the temporary local class if it exists
        const tempId = action.meta.arg.id;
        if (tempId) {
          state.classes = state.classes.filter(c => c.id !== tempId);
        }
        state.classes.push(newClass);
      })
      .addCase(createClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateClass.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateClass.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.classes.findIndex(
          (c) => c.id === action.payload._id || c._id === action.payload._id || c.id === action.payload.id
        );
        if (index !== -1) {
          state.classes[index] = { ...action.payload, id: action.payload._id || action.payload.id };
        } else {
          // If not found by _id, try to find by the original id and update
          const originalId = action.meta.arg.id;
          const originalIndex = state.classes.findIndex(
            (c) => c.id === originalId || c._id === originalId
          );
          if (originalIndex !== -1) {
            state.classes[originalIndex] = { ...action.payload, id: action.payload._id || action.payload.id };
          }
        }
      })
      .addCase(updateClass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteClass.fulfilled, (state, action) => {
        state.classes = state.classes.filter(
          (c) => c.id !== action.payload && c._id !== action.payload
        );
      });
  },
});

export const {
  addClassLocal,
  updateClassLocal,
  removeClassLocal,
  initializeSampleData,
} = classesSlice.actions;
export default classesSlice.reducer;
